import { useMemo } from 'react'
import Link from 'next/link'
import { Anchor, Box, Center, Text } from '@mantine/core'

import { WIND_DOWN } from '@/config'
import { useMediumScreen } from '@/hooks'
import { useAnnouncementFlags } from './queries'
import { useSelectedDenom } from '../Stake'

const GlobalAnnouncement = () => {
  const { data } = useAnnouncementFlags()

  const [selectedCoinDenom] = useSelectedDenom()

  const { isMediumScreen } = useMediumScreen()

  // We need to provide a way to display custom messages for specific host zone.
  // These messages can be used to inform users about important updates or issues related to affected host zone.
  // We can add a custom message for a host zone by adding a new entry to the CUSTOM_MESSAGES object.
  // We should move this to a more generic location in the future but this works for now.

  const CUSTOM_MESSAGES: Record<
    string,
    {
      showMessage: boolean
      message: JSX.Element
    }
  > = {
    ATOM: {
      showMessage: false,
      message: (
        <>
          Some stATOM unbondings are delayed until 3pm ET on Wednesday, August 7th (learn more{' '}
          <Anchor href="https://www.stride.zone/blog/statom-unbondings" target="_blank">
            here
          </Anchor>
          ).
        </>
      )
    },
    CMDX: {
      showMessage: true,
      message: (
        <>
          CMDX and stCMDX liquid staking are no longer supported on Stride. The Comdex chain has halted, and we are
          currently unable to reach the Comdex team.
        </>
      )
    }
  }

  const message = useMemo(() => {
    if (selectedCoinDenom in CUSTOM_MESSAGES) {
      const { showMessage, message } = CUSTOM_MESSAGES[selectedCoinDenom]

      if (showMessage) {
        return message
      }
    }

    if (data?.custom) {
      return data.customMessage
    }

    if (data?.maintenance && data?.upgrade) {
      return 'Stride is currently undergoing maintenance and a scheduled upgrade, performance degradation may occur.'
    }

    if (data?.upgrade) {
      return 'Stride is currently undergoing a scheduled upgrade, performance degradation may occur.'
    }

    if (data?.maintenance) {
      return 'Stride is currently undergoing maintenance, and certain functionality may be affected. Everything should be normal soon!'
    }

    return ''
  }, [data, selectedCoinDenom])

  // The wind-down notice takes priority over everything else, including Redis-driven messages.
  if (WIND_DOWN.isLiquidStakingDisabled) {
    return (
      <Box sx={(t) => ({ background: t.colors.pink[6], padding: t.other.globalAnnouncement.padding })}>
        <Center>
          <Text inline={isMediumScreen} align="center" sx={(t) => ({ color: t.white })}>
            <strong>Stride is winding down.</strong> Liquid staking and new redemptions are closed. Your stTokens are
            not lost: they will be redeemable on Osmosis at a fixed rate, with no deadline.{' '}
            <Link href="/#wind-down" passHref legacyBehavior>
              <Anchor sx={(t) => ({ color: t.white, fontWeight: 'bold', textDecoration: 'underline' })}>
                What this means for your funds
              </Anchor>
            </Link>
          </Text>
        </Center>
      </Box>
    )
  }

  if (!message) {
    return null
  }

  // if (!message) {
  //   // return null

  //   // context: We want to display some sort of announcement for the upcoming Stride Swap website.
  //   // If we have new cases like this in the future, we should consider using upstash or similar for dynamic message management (e.g. `marketing_announcements`)
  //   return (
  //     <Box sx={(t) => ({ background: t.colors.pink[6], padding: t.other.globalAnnouncement.padding })}>
  //       <Center>
  //         <Text inline={isMediumScreen} sx={(t) => ({ color: t.white })}>
  //           Comdex liquid staking will be unavailable starting in November. Read more about it{' '}
  //           <Anchor
  //             href="https://x.com/ComdexOfficial/status/1968366352537682220"
  //             target="_blank"
  //             sx={(t) => ({ color: t.white, fontWeight: 'bold', textDecoration: 'underline' })}>
  //             here
  //           </Anchor>
  //         </Text>
  //       </Center>
  //     </Box>
  //   )
  // }

  return (
    <Box sx={(t) => ({ background: t.colors.yellow[1], padding: t.other.globalAnnouncement.padding })}>
      <Center>
        <Text inline={isMediumScreen} sx={(t) => ({ color: t.colors.gray[7] })}>
          {message}
        </Text>
      </Center>
    </Box>
  )
}

export { GlobalAnnouncement }
